import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { apiBaseUrl } from '../../util/api';

import { fetchCurrentUser } from '../../ducks/user.duck';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/ManageListingsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/ManageListingsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/ManageListingsPage/DISCARD_DRAFT_ERROR';

export const RESERVE_LISTING_REQUEST = 'app/ManageListingsPage/RESERVE_LISTING_REQUEST';
export const RESERVE_LISTING_SUCCESS = 'app/ManageListingsPage/RESERVE_LISTING_SUCCESS';
export const RESERVE_LISTING_ERROR = 'app/ManageListingsPage/RESERVE_LISTING_ERROR';

export const UNRESERVE_LISTING_REQUEST = 'app/ManageListingsPage/UNRESERVE_LISTING_REQUEST';
export const UNRESERVE_LISTING_SUCCESS = 'app/ManageListingsPage/UNRESERVE_LISTING_SUCCESS';
export const UNRESERVE_LISTING_ERROR = 'app/ManageListingsPage/UNRESERVE_LISTING_ERROR';

export const SOLD_LISTING_REQUEST = 'app/ManageListingsPage/SOLD_LISTING_REQUEST';
export const SOLD_LISTING_SUCCESS = 'app/ManageListingsPage/SOLD_LISTING_SUCCESS';
export const SOLD_LISTING_ERROR = 'app/ManageListingsPage/SOLD_LISTING_ERROR';

export const UNSOLD_LISTING_REQUEST = 'app/ManageListingsPage/UNSOLD_LISTING_REQUEST';
export const UNSOLD_LISTING_SUCCESS = 'app/ManageListingsPage/UNSOLD_LISTING_SUCCESS';
export const UNSOLD_LISTING_ERROR = 'app/ManageListingsPage/UNSOLD_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';
export const CLEAR_OPEN_LISTING_ERROR = 'app/ManageListingsPage/CLEAR_OPEN_LISTING_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  discardingDraft: null,
  discardingDraftError: null,
  reservingListing: null,
  reservingListingError: null,
  unreservingListing: null,
  unreservingListingError: null,
  soldListing: null,
  soldListingError: null,
  unsoldListing: null,
  unsoldListingError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  // Deep-merge attributes so partial updates (e.g. only state/reserved/sold)
  // don't wipe out categoryLevel1, listing_level, and other publicData fields
  // that are needed for free-tier detection on the next open/close attempt.
  const updatedListing = {
    ...oldListing,
    attributes: {
      ...oldListing?.attributes,
      ...listingEntity.attributes,
      publicData: {
        ...oldListing?.attributes?.publicData,
        ...listingEntity.attributes?.publicData,
      },
    },
  };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_OPEN_LISTING_ERROR:
      return {
        ...state,
        openingListing: null,
        openingListingError: null,
      };

    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS:
      return {
        ...state,
        discardingDraft: null,
      };
    case DISCARD_DRAFT_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: {
          listingId: state.discardingDraft,
          error: payload,
        },
      };
    }

    case RESERVE_LISTING_REQUEST:
      return {
        ...state,
        reservingListing: payload.listingId,
        reservingListingError: null,
      };
    case RESERVE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        reservingListing: null,
      };
    case RESERVE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        reservingListing: null,
        reservingListingError: {
          listingId: state.reservingListing,
          error: payload,
        },
      };
    }

    case UNRESERVE_LISTING_REQUEST:
      return {
        ...state,
        unreservingListing: payload.listingId,
        unreservingListingError: null,
      };
    case UNRESERVE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        unreservingListing: null,
      };
    case UNRESERVE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        unreservingListing: null,
        unreservingListingError: {
          listingId: state.unreservingListing,
          error: payload,
        },
      };
    }

    case SOLD_LISTING_REQUEST:
      return {
        ...state,
        soldListing: payload.listingId,
        soldListingError: null,
      };
    case SOLD_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        soldListing: null,
      };
    case SOLD_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        soldListing: null,
        soldListingError: {
          listingId: state.soldListing,
          error: payload,
        },
      };
    }

    case UNSOLD_LISTING_REQUEST:
      return {
        ...state,
        unsoldListing: payload.listingId,
        unsoldListingError: null,
      };
    case UNSOLD_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        unsoldListing: null,
      };
    case UNSOLD_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        unsoldListing: null,
        unsoldListingError: {
          listingId: state.unsoldListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

export const clearOpenListingError = () => ({
  type: CLEAR_OPEN_LISTING_ERROR,
});

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = () => ({
  type: DISCARD_DRAFT_SUCCESS,
});

export const discardDraftError = e => ({
  type: DISCARD_DRAFT_ERROR,
  error: true,
  payload: e,
});

export const reserveListingRequest = listingId => ({
  type: RESERVE_LISTING_REQUEST,
  payload: { listingId },
});

export const reserveListingSuccess = response => ({
  type: RESERVE_LISTING_SUCCESS,
  payload: response.data,
});

export const reserveListingError = e => ({
  type: RESERVE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const unreserveListingRequest = listingId => ({
  type: UNRESERVE_LISTING_REQUEST,
  payload: { listingId },
});

export const unreserveListingSuccess = response => ({
  type: UNRESERVE_LISTING_SUCCESS,
  payload: response.data,
});

export const unreserveListingError = e => ({
  type: UNRESERVE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const soldListingRequest = listingId => ({
  type: SOLD_LISTING_REQUEST,
  payload: { listingId },
});

export const soldListingSuccess = response => ({
  type: SOLD_LISTING_SUCCESS,
  payload: response.data,
});

export const soldListingError = e => ({
  type: SOLD_LISTING_ERROR,
  error: true,
  payload: e,
});

export const unsoldListingRequest = listingId => ({
  type: UNSOLD_LISTING_REQUEST,
  payload: { listingId },
});

export const unsoldListingSuccess = response => ({
  type: UNSOLD_LISTING_SUCCESS,
  payload: response.data,
});

export const unsoldListingError = e => ({
  type: UNSOLD_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  const baseUrl = apiBaseUrl();

  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          reserved: false,
          sold: false,
        },
      },
      { expand: true }
    )
    .then(() => {
      return fetch(`${baseUrl}/api/cancel-listing-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          listingId: listingId.uuid,
          reason: 'user_closed',
        }),
      });
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      return response.json();
    })
    .then(() => {
      const listingEntity = {
        id: listingId,
        attributes: {
          state: 'closed',
          publicData: {
            reserved: false,
            sold: false,
          },
        },
      };

      console.log('Subscription cancelled, listing closed, reserved and sold reset...', listingEntity);

      const response = { data: { data: listingEntity } };
      dispatch(closeListingSuccess(response));

      // Refresh current user data to get the most up-to-date listing state
      dispatch(fetchCurrentUser());

      return { success: true };
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
      throw e;
    });

  // COMMENTED OUT: Complex subscription cancellation logic
  // // First cancel the subscription, then close the listing
  // const baseUrl = apiBaseUrl();
  //
  // return fetch(`${baseUrl}/api/cancel-listing-subscription`, {
  //   method: 'POST',
  //   headers: {
  //     'Content-Type': 'application/json',
  //   },
  //   body: JSON.stringify({
  //     listingId: listingId.uuid,
  //     reason: 'user_closed',
  //   }),
  // })
  //   .then(response => {
  //     if (!response.ok) {
  //       throw new Error('Failed to cancel subscription');
  //     }
  //     return response.json();
  //   })
  //   .then(() => {
  //     // After successful subscription cancellation, close the listing
  //     return sdk.ownListings
  //       .close({ id: listingId }, { expand: true });
  //   })
  //   .then(response => {
  //     dispatch(closeListingSuccess(response));
  //     // Refresh current user data to get updated listings and subscriptions
  //     dispatch(fetchCurrentUser());
  //     return response;
  //   })
  //   .catch(e => {
  //     dispatch(closeListingError(storableError(e)));
  //   });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  // Reactivate endpoint handles BOTH free and paid tiers:
  //   - Free tier → server opens the listing directly and responds
  //     with { success: true, opened: true }. No Stripe redirect.
  //   - Paid tier → server creates a Stripe checkout and responds
  //     with { success: true, checkoutUrl: '...' }. We redirect.
  const baseUrl = apiBaseUrl();

  return fetch(`${baseUrl}/api/reactivate-listing-subscription`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      listingId: listingId.uuid,
    }),
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }
      return response.json();
    })
    .then(reactivationData => {
      // ============================================================
      // LEGACY GATE (pre-2026-04) — PRESERVED FOR POTENTIAL REVERT
      // Original behavior: only the Stripe-redirect branch was
      // handled. For free listings the server now returns
      // { success: true, opened: true } with no checkoutUrl, which
      // fell into the else branch and threw "Failed to create
      // checkout session" even though the server had successfully
      // opened the listing. Per client request (Apr 2026, Ross via
      // Slack), free tiers must reopen with no Stripe interaction.
      // To revert: replace the active block below with the legacy
      // one.
      //
      //   if (reactivationData.success && reactivationData.checkoutUrl) {
      //     window.location.href = reactivationData.checkoutUrl;
      //     return { redirected: true };
      //   } else {
      //     throw new Error('Failed to create checkout session');
      //   }
      // ============================================================

      // Paid tier — server returned a Stripe checkout URL, redirect.
      if (reactivationData.success && reactivationData.checkoutUrl) {
        window.location.href = reactivationData.checkoutUrl;
        return { redirected: true };
      }

      // Free tier — server opened the listing directly. Update
      // local state (same pattern as closeListing above) and
      // refresh currentUser so the listings list re-renders.
      if (reactivationData.success && reactivationData.opened) {
        const listingEntity = {
          id: listingId,
          attributes: {
            state: 'published',
          },
        };
        const response = { data: { data: listingEntity } };
        dispatch(openListingSuccess(response));
        dispatch(fetchCurrentUser());
        return { success: true, opened: true };
      }

      throw new Error('Failed to reactivate listing: unexpected response');
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
      throw e;
    });

  // COMMENTED OUT: Complex subscription checking logic
  // // Always check subscription first before opening any listing
  // const baseUrl = apiBaseUrl();
  // const currentUser = getState().user.currentUser;
  //
  // if (!currentUser || !currentUser.id) {
  //   dispatch(openListingError(storableError(new Error('User not logged in'))));
  //   return Promise.reject(new Error('User not logged in'));
  // }
  //
  // // Check user subscriptions to see if this listing has an active subscription
  // return fetch(`${baseUrl}/api/user-subscriptions?userId=${currentUser.id.uuid}`)
  //   .then(response => {
  //     if (!response.ok) {
  //       throw new Error('Failed to load user subscriptions');
  //     }
  //     return response.json();
  //   })
  //   .then(subscriptionData => {
  //     // Check if the listing has an active subscription
  //     const allSubscriptions = subscriptionData.subscriptionsFlat || [];
  //     const listingSubscription = allSubscriptions.find(sub =>
  //       sub.listingId === listingId.uuid && sub.subscriptionStatus === 'active'
  //     );
  //
  //     if (listingSubscription) {
  //       // Has active subscription, proceed to open the listing
  //       return sdk.ownListings
  //         .open({ id: listingId }, { expand: true })
  //         .then(response => {
  //           dispatch(openListingSuccess(response));
  //           // Refresh current user data to get updated listings
  //           dispatch(fetchCurrentUser());
  //           return response;
  //         });
  //     } else {
  //       // No active subscription, require payment first
  //       return fetch(`${baseUrl}/api/reactivate-listing-subscription`, {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify({
  //           listingId: listingId.uuid,
  //         }),
  //       })
  //         .then(response => {
  //           if (!response.ok) {
  //             throw new Error('Failed to create subscription checkout');
  //           }
  //           return response.json();
  //         })
  //         .then(reactivationData => {
  //           if (reactivationData.success && reactivationData.checkoutUrl) {
  //             // Redirect to Stripe checkout for payment
  //             window.location.href = reactivationData.checkoutUrl;
  //             return { redirected: true };
  //           } else {
  //             throw new Error('Failed to create checkout session');
  //           }
  //         });
  //     }
  //   })
  //   .catch(e => {
  //     dispatch(openListingError(storableError(e)));
  //     throw e;
  //   });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
export const discardDraft = listingId => (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));
  const { queryParams } = getState().ManageListingsPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => {
      // Return the listing update with a delay, so that the user
      // notices which listing gets removed
      return Promise.all([delay(300), sdk.ownListings.query(queryParams)]);
    })
    .then(([_, listingResponse]) => {
      dispatch(addOwnEntities(listingResponse));
      dispatch(queryListingsSuccess(listingResponse));
      dispatch(discardDraftSuccess());
    })
    .catch(e => {
      dispatch(discardDraftError(storableError(e)));
    });
};

export const reserveListing = listingId => (dispatch, getState, sdk) => {
  dispatch(reserveListingRequest(listingId));

  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          reserved: true,
          sold: false,
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(reserveListingSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(reserveListingError(storableError(e)));
      throw e;
    });
};

export const unreserveListing = listingId => (dispatch, getState, sdk) => {
  dispatch(unreserveListingRequest(listingId));

  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          reserved: false,
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(unreserveListingSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(unreserveListingError(storableError(e)));
      throw e;
    });
};

export const soldListing = listingId => (dispatch, getState, sdk) => {
  dispatch(soldListingRequest(listingId));

  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          sold: true,
          reserved: false,
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(soldListingSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(soldListingError(storableError(e)));
      throw e;
    });
};

export const unsoldListing = listingId => (dispatch, getState, sdk) => {
  dispatch(unsoldListingRequest(listingId));

  return sdk.ownListings
    .update(
      {
        id: listingId,
        publicData: {
          sold: false,
        },
      },
      { expand: true }
    )
    .then(response => {
      dispatch(unsoldListingSuccess(response));
      dispatch(fetchCurrentUser());
      return response;
    })
    .catch(e => {
      dispatch(unsoldListingError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListings({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
  ])
    .then(response => {
      // const currentUser = response[0]?.data?.data;
      const ownListings = response[1]?.data?.data;
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
